import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import {
  ChatTextIcon,
  CheckCircleIcon,
  ArrowsClockwiseIcon,
  BuildingsIcon,
  CaretLeftIcon,
  CaretRightIcon,
  NotePencilIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TextInput } from '@/components/common/TextInput';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import useCustomer from '@/api/accounts-receivable/customers/useCustomer';
import {
  invalidateThreadQueries,
  useArCollectionThread,
  useArCollectionThreadsRanked,
  useAddArCollectionThreadComment,
  useReopenArCollectionThread,
  useResolveArCollectionThread,
  useUpdateArCollectionThread,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useRefreshArCollectionThreadFromFamous } from '@/api/rpc/hooks/accounts-receivable/useRefreshArCollectionThreadFromFamous';
import { useReevaluateArCollectionThread } from '@/api/rpc/hooks/accounts-receivable/useReevaluateArCollectionThread';
import { formatCurrency } from '@/components/common/Currency';
import { ThreadAutoResolvedBanner } from './ThreadAutoResolvedBanner';
import { threadRefreshOutcome } from './threadRefreshOutcome';
import { CollectionThreadSetNextStepDialog } from './CollectionThreadSetNextStepDialog';
import { ThreadReminderBanner } from './ThreadReminderBanner';
import { CollectionEmailDraftPanel } from './CollectionEmailDraftDialog';
import { CollectionThreadEvidenceSection } from './CollectionThreadEvidenceSection';
import { CollectionThreadAgentActionsPanel } from './CollectionThreadAgentActionsPanel';
import { PortalActionRunStatus } from './PortalActionRunStatus';
import { CollectionThreadActivityTimeline } from './CollectionThreadActivityTimeline';
import { ShortPaySummaryCard } from './ShortPaySummaryCard';
import { ShortPayRecommendationCard } from './ShortPayRecommendationCard';
import { OverPaymentSummaryCard } from './OverPaymentSummaryCard';
import { OverPaymentRecommendationCard } from './OverPaymentRecommendationCard';
import { Watchers } from '@/components/watchers/Watchers';
import { UploadProgressList } from '@/components/documents/UploadProgressList';
import { cn } from '@/lib/utils';
import { useToast } from 'src/context/ToastContext';
import { useAgentModule } from '@/hooks/useAgentModule';
import { useFileDrop } from '@/hooks/useFileDrop';
import { useUploadThreadEvidence } from './useUploadThreadEvidence';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import {
  CollectionThreadRemediationMetadataSchema,
  formatCollectionThreadCode,
} from '@erp/rpc';
import type {
  CollectionThreadResolution,
  CollectionThreadType,
  CollectionThreadWithActivitiesDto,
} from '@erp/rpc';
import {
  TYPE_LABELS,
  RESOLUTION_OPTIONS,
  RESOLUTION_LABELS,
} from './threadLabels';
import { DeductionValidationCard } from './DeductionValidationCard';

/** Narrow a run_portal_action tool result to one that actually started a run. */
function isStartedPortalRun(
  result: unknown,
): result is { started: true; workflowRunId: string; label?: string | null } {
  if (typeof result !== 'object' || result === null) return false;
  if (!('started' in result) || !('workflowRunId' in result)) return false;
  return result.started === true && typeof result.workflowRunId === 'string';
}

interface CollectionThreadDetailViewProps {
  threadId: string;
  /** Marks which ranked list the user arrived from; drives Back + Prev/Next. */
  from?: 'summary' | 'threads';
  /**
   * Hide the back/Prev-Next nav and the "Resolved" status pill row. Used by
   * the inbox right pane where the surrounding chrome already handles those.
   */
  hideHeaderNav?: boolean;
  /**
   * Notified when the inline email composer opens (true) or closes (false). The
   * master-detail list uses this to auto-collapse its thread rail so the
   * composer gets the room.
   */
  onDraftOpenChange?: (open: boolean) => void;
}

export function CollectionThreadDetailView({
  threadId,
  from,
  hideHeaderNav,
  onDraftOpenChange,
}: CollectionThreadDetailViewProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const { thread, isLoading } = useArCollectionThread(threadId);

  const customerId = thread?.customerId ?? null;
  // Customer name isn't on the thread DTO — pull it from the customer record so
  // the header shows the Famous customer ID as `Name (ABC)` (erpDisplayName),
  // matching the cash-receipt customer picker and the threads list.
  const { data: customerData } = useCustomer(customerId ?? undefined);
  const customerName =
    customerData?.customer.erpDisplayName ??
    customerData?.customer.name ??
    null;

  // ── Open-time Famous refresh ──────────────────────────────────────────────
  // Pull the linked invoice's latest header state (balance, paid, status) AND
  // the Famous cash receipts applied to it when the thread opens. The backend
  // re-derives the thread from those fresh numbers in the same call —
  // regenerating the title, and auto-resolving the thread outright when the
  // invoice comes back paid in full — so the user watches the thread settle
  // instead of finding out at tomorrow's ingest. Best-effort: on any failure
  // the view simply keeps showing already-ingested data.
  const queryClient = useQueryClient();
  const { mutateAsync: refreshThreadFromFamous } =
    useRefreshArCollectionThreadFromFamous();
  // Once per (thread, linked invoice) per mount; added optimistically so a
  // StrictMode double-invoked effect can't fire twice, rolled back on failure
  // so a later trigger can retry (same pattern as BulkSendStatementDialog).
  // The invoice id is part of the key on purpose: relinking a thread to a
  // different invoice has to pull that invoice's state, and a thread-only key
  // would suppress it until the view remounted. The server holds a matching
  // per-(thread, invoice) cooldown, so remounts and refocus still can't turn
  // into a stream of Oracle queries.
  const refreshedKeysRef = useRef<Set<string>>(new Set());
  const threadInvoiceId = thread?.invoiceId ?? null;
  // Set when the refresh resolved the thread out from under the user, so the
  // banner can explain it. Cleared by dismissing.
  //
  // Carries the `(thread, invoice)` key it was produced for: the refresh is
  // async, and navigating to another thread while one is in flight would
  // otherwise land this response on whatever thread is now displayed —
  // announcing that *this* thread resolved when a different one did. The key is
  // checked again at render, so the notice also disappears on navigation
  // instead of following the user.
  const [autoResolvedNotice, setAutoResolvedNotice] = useState<{
    refreshKey: string;
    invoiceNumber: string | null;
    balanceBefore: string | null;
  } | null>(null);
  useEffect(() => {
    if (!threadInvoiceId) return;
    const refreshKey = `${threadId}:${threadInvoiceId}`;
    if (refreshedKeysRef.current.has(refreshKey)) return;
    refreshedKeysRef.current.add(refreshKey);
    // Flipped by the cleanup below when the view switches threads (or unmounts)
    // while this request is still open — that answer is about a thread the user
    // has already left.
    let cancelled = false;
    refreshThreadFromFamous({ id: threadId })
      .then((result) => {
        if (cancelled) return;
        // Tiered by consequence — see `threadRefreshOutcome`. A refresh that
        // changed nothing renders and fires nothing.
        const outcome = threadRefreshOutcome(result);
        if (outcome === 'none') return;

        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arInvoices.get.key({
            input: { id: threadInvoiceId },
          }),
        });
        // Refetches the thread (regenerated title, and the resolved status +
        // its new status_change activity when the invoice came back settled),
        // plus the evidence graph the new payments belong to.
        invalidateThreadQueries(queryClient);

        if (outcome === 'resolved') {
          setAutoResolvedNotice({
            refreshKey,
            invoiceNumber: result.invoiceNumber,
            balanceBefore: result.balanceBefore,
          });
          return;
        }

        showToast({
          title: gt('Updated from Famous', {
            context:
              'toast title, the thread invoice picked up new data from the Famous ERP',
          }),
          message:
            result.newPaymentsApplied > 0
              ? gt('{count} new payments applied', {
                  count: result.newPaymentsApplied,
                  context:
                    'toast message, number of payments newly applied to the invoice',
                })
              : gt('Balance now {balance}', {
                  balance: formatCurrency(result.balanceAfter ?? '0', {
                    currency: 'USD',
                  }),
                  context: 'toast message, the invoice balance after a refresh',
                }),
          color: 'info',
          icon: 'info',
        });
      })
      .catch(() => {
        refreshedKeysRef.current.delete(refreshKey);
      });
    return () => {
      cancelled = true;
    };
  }, [
    threadId,
    threadInvoiceId,
    refreshThreadFromFamous,
    queryClient,
    showToast,
    gt,
  ]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!thread || !customerId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        <T context="empty state, thread not found">
          We couldn't find that collection thread.
        </T>
      </div>
    );
  }

  return (
    <CollectionThreadBody
      key={thread.id}
      thread={thread}
      customerId={customerId}
      customerName={customerName}
      from={from}
      hideHeaderNav={hideHeaderNav}
      onDraftOpenChange={onDraftOpenChange}
      autoResolvedNotice={
        // Only ever render the notice against the thread + invoice it was
        // produced for, so a resolution announced for one thread can't appear
        // on the next one the user opens.
        autoResolvedNotice?.refreshKey === `${threadId}:${threadInvoiceId}`
          ? autoResolvedNotice
          : null
      }
      onDismissAutoResolvedNotice={() => setAutoResolvedNotice(null)}
    />
  );
}

interface BodyProps {
  thread: CollectionThreadWithActivitiesDto;
  customerId: string;
  customerName: string | null;
  from?: 'summary' | 'threads';
  hideHeaderNav?: boolean;
  onDraftOpenChange?: (open: boolean) => void;
  /** Set when the open-time Famous refresh resolved this thread. */
  autoResolvedNotice: {
    refreshKey: string;
    invoiceNumber: string | null;
    balanceBefore: string | null;
  } | null;
  onDismissAutoResolvedNotice: () => void;
}

function CollectionThreadBody({
  thread,
  customerId,
  customerName,
  from,
  hideHeaderNav,
  onDraftOpenChange,
  autoResolvedNotice,
  onDismissAutoResolvedNotice,
}: BodyProps) {
  const gt = useGT();

  // Surface Nami on the thread: the user can ask it to fetch backup / file a
  // claim through the customer's portal and it runs the configured action,
  // filing the results onto this thread as evidence.
  const threadReferenceCode = formatCollectionThreadCode(
    thread.referenceNumber,
  );
  // Render a live run-status card under the run_portal_action tool result:
  // progress + the operator's live browser view, and refresh evidence on
  // completion.
  const renderToolResult = useCallback(
    (_msg: Message, toolResult: MessageToolResult) => {
      if (toolResult.toolName !== 'run_portal_action') return null;
      const result = toolResult.result;
      if (!isStartedPortalRun(result)) return null;
      return (
        <PortalActionRunStatus
          workflowRunId={result.workflowRunId}
          label={result.label ?? undefined}
        />
      );
    },
    [],
  );
  useAgentModule({
    moduleKey: 'collections',
    currentPage: `/accounts-receivable/collections/${customerId}/threads/${thread.id}`,
    getModuleContext: () => ({
      threadId: thread.id,
      threadReferenceCode,
      threadReferenceNumber: thread.referenceNumber,
      customerName,
    }),
    renderToolResult,
    placeholder: gt(
      'e.g. "fetch the backup for check 99812 and invoice INV-4471"',
      {
        context: 'placeholder for the collections thread assistant chat input',
      },
    ),
  });
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Always-on evidence dropzone: the whole thread view accepts dropped files
  // (or Outlook emails) at any time, running the exact pipeline the Upload
  // dialog uses — same validation, routing and query invalidation — via the
  // shared useUploadThreadEvidence hook.
  const evidenceUpload = useUploadThreadEvidence(thread.id);

  function handleDroppedEvidenceFiles(files: File[]) {
    void evidenceUpload.uploadFiles(files).then(({ failed }) => {
      // Per-file success was already toasted by the upload mutation; only keep
      // the floating progress panel around when a row failed, so the rep can
      // read which file and why.
      if (failed === 0) evidenceUpload.resetProgress();
    });
  }

  const { isDragActive, dropTargetProps } = useFileDrop({
    onDropFiles: handleDroppedEvidenceFiles,
    // One batch at a time — mirrors FileUploadDropzone refusing drops while
    // an upload is in flight.
    disabled: evidenceUpload.isUploading,
  });

  const [comment, setComment] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);

  // The inline email composer lives in the thread view (instead of a modal) so
  // the clerk can read the thread's documents while drafting. A single active
  // draft is owned here; the summary and recommendation cards call `openDraft`
  // to open it — the recommendation cards seed a prompt/recipients, the summary
  // cards open it blank (auto/describe intake). Null = collapsed.
  const [activeDraft, setActiveDraft] = useState<{
    initialPrompt?: string;
    initialToRecipients?: string[];
  } | null>(null);
  const draftPanelRef = useRef<HTMLDivElement>(null);

  function openDraft(seed?: {
    initialPrompt?: string;
    initialToRecipients?: string[];
  }) {
    setActiveDraft(seed ?? {});
    onDraftOpenChange?.(true);
    // Scroll the panel into view once it has mounted. Double-rAF so the scroll
    // runs after the just-set state has committed and painted.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        draftPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
      ),
    );
  }

  function closeDraft() {
    setActiveDraft(null);
    onDraftOpenChange?.(false);
  }

  // Reveal the collapsed note composer, then scroll it into view and focus the
  // textarea once it has rendered. Also invoked by the summary cards' "Add note".
  function openComposer() {
    // Resolved threads are read-only; every entry point (including the summary
    // cards' "Add note") should be a no-op rather than expand a disabled composer.
    if (isResolved) return;
    setComposerOpen(true);
    requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      composerRef.current?.querySelector('textarea')?.focus();
    });
  }

  function closeComposer() {
    setComposerOpen(false);
    setComment('');
  }
  const [selectedResolution, setSelectedResolution] =
    useState<CollectionThreadResolution>('paid_in_full');
  const [resolveComment, setResolveComment] = useState('');

  const addCommentMutation = useAddArCollectionThreadComment();
  const resolveMutation = useResolveArCollectionThread();
  const reopenMutation = useReopenArCollectionThread();
  const updateMutation = useUpdateArCollectionThread();
  const reevaluateMutation = useReevaluateArCollectionThread();

  // Reset the resolve sub-form when status changes.
  useEffect(() => {
    if (thread.status !== 'open') {
      setResolveOpen(false);
      setResolveComment('');
    }
  }, [thread.status]);

  const isResolved = thread.status === 'resolved';
  const isBusy =
    addCommentMutation.isPending ||
    resolveMutation.isPending ||
    reopenMutation.isPending ||
    // A re-evaluation can resolve the thread out from under the user, so the
    // status-changing controls stand down until it lands.
    reevaluateMutation.isPending;

  async function handleInvoiceChange(nextInvoiceId: string | null) {
    if (nextInvoiceId === thread.invoiceId) return;
    try {
      await updateMutation.mutateAsync({
        id: thread.id,
        invoiceId: nextInvoiceId,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not update invoice link', {
              context: 'toast message, invoice link update failed',
            });
      showToast({
        title: gt('Update failed', {
          context: 'toast title, invoice link update failed',
        }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  async function handleAddComment() {
    const body = comment.trim();
    if (!body) return;
    try {
      await addCommentMutation.mutateAsync({ id: thread.id, body });
      closeComposer();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not add comment', {
              context: 'toast message, add comment failed',
            });
      showToast({
        title: gt('Comment failed', {
          context: 'toast title, add comment failed',
        }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  async function handleResolve() {
    try {
      await resolveMutation.mutateAsync({
        id: thread.id,
        resolution: selectedResolution,
        comment: resolveComment.trim() || undefined,
      });
      setResolveOpen(false);
      setResolveComment('');
      showToast({
        title: gt('Thread resolved', {
          context: 'toast title, thread resolved',
        }),
        message: RESOLUTION_LABELS[selectedResolution],
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not resolve thread', {
              context: 'toast message, resolve failed',
            });
      showToast({
        title: gt('Resolve failed', { context: 'toast title, resolve failed' }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  /**
   * Resolve a deduction thread straight from the validation card — Approve maps
   * to `deduction_approved`, Charge-back to `deduction_charged_back`. Skips the
   * resolution-picker dialog the generic flow uses.
   */
  async function handleResolveDeduction(
    resolution: CollectionThreadResolution,
  ) {
    try {
      await resolveMutation.mutateAsync({ id: thread.id, resolution });
      showToast({
        title: gt('Deduction updated', {
          context: 'toast title, deduction validated',
        }),
        message: RESOLUTION_LABELS[resolution],
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not update deduction', {
              context: 'toast message, deduction validation failed',
            });
      showToast({
        title: gt('Update failed', {
          context: 'toast title, deduction validation failed',
        }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  /**
   * Re-derive the page from source: Famous, then evidence, then the AI
   * recommendation. The backend runs the chain in order and reports each stage
   * separately, so the toast can be specific about what actually moved — "we
   * refreshed" tells a collector nothing they can check.
   */
  async function handleReevaluate() {
    try {
      const result = await reevaluateMutation.mutateAsync({ id: thread.id });
      const parts: string[] = [];
      if (result.famous.balanceChanged && result.famous.balanceAfter) {
        parts.push(
          gt('balance now {balance}', {
            balance: formatCurrency(result.famous.balanceAfter, {
              currency: 'USD',
            }),
            context: 'toast fragment, the invoice balance after re-evaluation',
          }),
        );
      }
      if (result.famous.newPaymentsApplied > 0) {
        parts.push(
          gt('{count} new payments', {
            count: result.famous.newPaymentsApplied,
            context: 'toast fragment, payments newly applied to the invoice',
          }),
        );
      }
      if (result.evidenceLinked + result.emailsLinked > 0) {
        parts.push(
          gt('{count} new evidence items', {
            count: result.evidenceLinked + result.emailsLinked,
            context: 'toast fragment, evidence rows newly linked to the thread',
          }),
        );
      }
      if (result.recommendationRegenerated) {
        parts.push(
          gt('recommendation refreshed', {
            context: 'toast fragment, the AI recommended action was rebuilt',
          }),
        );
      }
      showToast({
        title: result.partial
          ? gt('Partially re-evaluated', {
              context:
                'toast title, some stages of the thread re-evaluation failed',
            })
          : gt('Thread re-evaluated', {
              context: 'toast title, thread re-evaluation finished',
            }),
        message:
          parts.length > 0
            ? parts.join(' · ')
            : gt('Everything was already up to date', {
                context:
                  'toast message, the re-evaluation found nothing to change',
              }),
        color: result.partial ? 'warning' : 'success',
        icon: result.partial ? 'warning' : 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not re-evaluate this thread', {
              context: 'toast message, re-evaluation failed',
            });
      showToast({
        title: gt('Re-evaluation failed', {
          context: 'toast title, re-evaluation failed',
        }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  async function handleReopen() {
    try {
      await reopenMutation.mutateAsync({ id: thread.id });
      showToast({
        title: gt('Thread reopened', {
          context: 'toast title, thread reopened',
        }),
        message: '',
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Could not reopen thread', {
              context: 'toast message, reopen failed',
            });
      showToast({
        title: gt('Reopen failed', { context: 'toast title, reopen failed' }),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  const openedRelative = (() => {
    const parsed = parseISO(thread.createdAt);
    if (Number.isNaN(parsed.getTime())) return thread.createdAt;
    return formatDistanceToNow(parsed, { addSuffix: true });
  })();
  const resolvedRelative = (() => {
    if (!thread.resolvedAt) return null;
    const parsed = parseISO(thread.resolvedAt);
    if (Number.isNaN(parsed.getTime())) return thread.resolvedAt;
    return formatDistanceToNow(parsed, { addSuffix: true });
  })();

  const headerActions = (
    <div className="flex flex-col items-end gap-1.5">
      {!hideHeaderNav &&
        (from ? (
          <ThreadListNav from={from} currentThreadId={thread.id} />
        ) : (
          <Link
            to="/accounts-receivable/collections/$customerId"
            params={{ customerId }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr;{' '}
            <T context="navigation, back to customer">
              Back to <Var>{customerName ?? 'customer'}</Var>
            </T>
          </Link>
        ))}
      <div className="flex items-center gap-1.5">
        <Watchers
          watchableEntityType="collection_thread"
          watchableId={thread.id}
          size="sm"
          // Assignment lives in CoreWatch but is projected onto the thread's
          // assignee column server-side, so every watcher change must refresh
          // the thread reads (list Assignee column, timeline, counts).
          onWatcherChange={() => invalidateThreadQueries(queryClient)}
        />
        {/* Outside the open/resolved split on purpose: "did anything change on
            this invoice?" is a fair question about a resolved thread too, and
            it's the fastest way to find out a settled thread has come unsettled. */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleReevaluate}
          // Also blocked on an in-flight invoice relink: the whole chain reads
          // the thread's invoice, and starting it mid-save would derive the
          // card, the evidence and the recommendation from an invoice the
          // thread is no longer pointing at.
          disabled={reevaluateMutation.isPending || updateMutation.isPending}
          title={gt(
            'Re-pull the invoice from Famous, rebuild the evidence, and regenerate the recommended action',
            { context: 'tooltip, what the re-evaluate button does' },
          )}
        >
          <ArrowsClockwiseIcon
            className={`h-4 w-4 mr-1 ${reevaluateMutation.isPending ? 'animate-spin' : ''}`}
          />
          {reevaluateMutation.isPending ? (
            <T context="button label, thread re-evaluation in progress">
              Re-evaluating…
            </T>
          ) : (
            <T context="button label, re-derive the whole thread from source data">
              Re-evaluate
            </T>
          )}
        </Button>
        {isResolved ? (
          <Button
            variant="outline"
            onClick={handleReopen}
            disabled={isBusy}
            size="sm"
          >
            <ArrowsClockwiseIcon className="h-4 w-4 mr-1" />
            <T context="button label, reopen resolved thread">Reopen thread</T>
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => setNextStepOpen(true)}
            >
              <NotePencilIcon className="h-4 w-4 mr-1" />
              <T context="button label, record the thread's next step">
                Set next step
              </T>
            </Button>
            <Button
              onClick={() => setResolveOpen(true)}
              disabled={isBusy}
              size="sm"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              <T context="button label, resolve collection thread">
                Resolve thread
              </T>
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    // Detail page fills its host's bounded height: a fixed title bar
    // (flex-none) over a single scrolling content region. Every host — the
    // List/Inbox detail panes and the standalone route (via the shell's `main`)
    // — hands this a bounded box, so `h-full` resolves. `relative` anchors the
    // drag-and-drop overlay and the floating upload-progress panel; the drop
    // handlers make the whole view an always-on evidence drop target.
    <div className="relative flex h-full flex-col" {...dropTargetProps}>
      {/* Drag overlay — pointer-events-none so it never intercepts clicks (it
          only renders mid-drag anyway) and so drag events keep hitting the
          container underneath instead of churning the enter/leave counter. */}
      {isDragActive && (
        <div className="pointer-events-none absolute inset-0 z-50 bg-background/60 p-3">
          <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5">
            <p className="rounded-md bg-card px-4 py-2 text-lg font-semibold text-foreground shadow-sm">
              <T context="overlay label while dragging files over a collection thread, dropping uploads them as evidence">
                Drop files to add as evidence
              </T>
            </p>
          </div>
        </div>
      )}

      {/* Dropped-file upload progress — floating panel (the drop path has no
          dialog to host the rows). Auto-clears on all-success; sticks around
          on failure until dismissed. */}
      {evidenceUpload.progress.length > 0 && (
        <div className="absolute bottom-4 right-4 z-40 max-h-96 w-80 overflow-y-auto rounded-lg border bg-card p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              <T context="heading, floating panel showing upload progress for files dropped on the thread">
                Uploading evidence
              </T>
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={evidenceUpload.resetProgress}
              disabled={evidenceUpload.isUploading}
              aria-label={gt('Dismiss upload progress', {
                context:
                  'button label, close the floating upload progress panel',
              })}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          <UploadProgressList uploadProgress={evidenceUpload.progress} />
        </div>
      )}
      {/* Title bar — fixed at the top; the content below scrolls under it. Owns
          its own padding + border (no negative margins reaching into the host's
          gutter), so it lines up the same in every host. */}
      <div className="flex-none border-b bg-card px-6 py-3">
        <ThreadHeader
          referenceCode={thread.referenceCode}
          title={thread.title}
          description={thread.description}
          status={thread.status}
          type={thread.type}
          openedRelative={openedRelative}
          resolvedRelative={resolvedRelative}
          resolution={thread.resolution}
          createdAt={thread.createdAt}
          autoDetected={thread.metadata.autoDetected === true}
          customerId={customerId}
          customerName={customerName}
          metadata={thread.metadata}
          actions={headerActions}
        />
      </div>

      {/* Reminder banner — pinned between the title bar and the scroll region
          so it can't be scrolled away. Renders nothing (no empty box) unless
          the system has an outstanding task pointing the user back here. */}
      <ThreadReminderBanner
        threadId={thread.id}
        className="mx-6 mt-3 flex-none"
      />

      {/* Content — the single scroll region underneath the title bar. Owns its
          padding so the host container can stay unpadded and layout-agnostic. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {autoResolvedNotice && (
          <div className="mb-4">
            <ThreadAutoResolvedBanner
              invoiceNumber={autoResolvedNotice.invoiceNumber}
              balanceBefore={autoResolvedNotice.balanceBefore}
              onDismiss={onDismissAutoResolvedNotice}
            />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
          <div className="flex flex-col gap-3 min-w-0">
            {thread.type === 'short_pay' && (
              <ShortPaySummaryCard
                threadId={thread.id}
                invoiceId={thread.invoiceId}
                customerId={customerId}
                onInvoiceChange={handleInvoiceChange}
                invoiceUpdating={
                  updateMutation.isPending || reevaluateMutation.isPending
                }
                metadata={thread.metadata}
                isResolved={isResolved}
                onAddNote={openComposer}
                onDraftEmail={() => openDraft()}
              />
            )}
            {thread.type === 'over_payment' && (
              <OverPaymentSummaryCard
                threadId={thread.id}
                invoiceId={thread.invoiceId}
                customerId={customerId}
                onInvoiceChange={handleInvoiceChange}
                invoiceUpdating={
                  updateMutation.isPending || reevaluateMutation.isPending
                }
                metadata={thread.metadata}
                isResolved={isResolved}
                onAddNote={openComposer}
                onDraftEmail={() => openDraft()}
              />
            )}
            {thread.type === 'deduction' && (
              <DeductionValidationCard
                metadata={thread.metadata}
                resolution={thread.resolution}
                isResolved={isResolved}
                isPending={resolveMutation.isPending}
                onApprove={() => handleResolveDeduction('deduction_approved')}
                onChargeBack={() =>
                  handleResolveDeduction('deduction_charged_back')
                }
              />
            )}

            {/* Inline composer — rendered directly under the summary card so the
              clerk can read the thread's documents while drafting. */}
            <div ref={draftPanelRef}>
              <AnimatedReveal open={activeDraft != null}>
                {activeDraft ? (
                  <CollectionEmailDraftPanel
                    key={`${thread.id}:${activeDraft.initialPrompt ?? ''}`}
                    threadId={thread.id}
                    threadTitle={thread.title}
                    initialPrompt={activeDraft.initialPrompt}
                    initialToRecipients={activeDraft.initialToRecipients}
                    onClose={closeDraft}
                  />
                ) : null}
              </AnimatedReveal>
            </div>

            {thread.type === 'short_pay' && (
              <ShortPayRecommendationCard
                threadId={thread.id}
                onDraftEmail={openDraft}
              />
            )}
            {thread.type === 'over_payment' && (
              <OverPaymentRecommendationCard
                threadId={thread.id}
                onDraftEmail={openDraft}
              />
            )}

            <div className="rounded-lg border bg-card">
              <div className="flex items-center gap-2 px-3 py-2 border-b">
                <h2 className="text-sm font-semibold">
                  <T context="section title, activity feed">Activity</T>
                </h2>
                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                  {thread.activities.length}
                </Badge>
              </div>

              <div ref={composerRef} className="border-b">
                {composerOpen ? (
                  <div className="px-3 py-3 bg-muted/30">
                    <TextInput
                      value={comment}
                      onValueChange={setComment}
                      placeholder={gt('Add a note visible only to your team…', {
                        context: 'placeholder, collection thread comment input',
                      })}
                      rows={3}
                      disabled={isBusy || isResolved}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={closeComposer}
                        disabled={isBusy}
                      >
                        <T context="button label, cancel note">Cancel</T>
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAddComment}
                        disabled={
                          isBusy || isResolved || comment.trim().length === 0
                        }
                      >
                        <ChatTextIcon className="h-4 w-4 mr-1" />
                        <T context="button label, post comment">Comment</T>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openComposer}
                    disabled={isResolved}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChatTextIcon className="h-4 w-4" />
                    <T context="button label, open note composer">
                      Add a note…
                    </T>
                  </button>
                )}
              </div>

              <CollectionThreadActivityTimeline
                activities={thread.activities}
                invoiceEvents={thread.invoiceEvents}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 min-w-0">
            <CollectionThreadEvidenceSection threadId={thread.id} />
            <CollectionThreadAgentActionsPanel
              threadId={thread.id}
              customerId={customerId}
            />
          </div>
        </div>
      </div>

      <CollectionThreadSetNextStepDialog
        thread={
          nextStepOpen && !isResolved
            ? {
                id: thread.id,
                referenceCode: thread.referenceCode,
                title: thread.title,
                customerName,
                waitingOnKey: thread.waitingOnKey,
                followUpAt: thread.followUpAt,
              }
            : null
        }
        onOpenChange={(open) => {
          if (!open) setNextStepOpen(false);
        }}
      />

      <Dialog
        open={resolveOpen && !isResolved}
        onOpenChange={(open) => {
          // Don't let an outside-click/Esc close mid-request.
          if (isBusy) return;
          setResolveOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title, resolve collection thread">
                Resolve thread
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="dialog description, resolve collection thread">
                Close out this thread and record how it was settled.
              </T>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="resolve-resolution">
                <T context="form label, resolution outcome">Resolution</T>
              </Label>
              <Select
                value={selectedResolution}
                onValueChange={(v) =>
                  setSelectedResolution(v as CollectionThreadResolution)
                }
              >
                <SelectTrigger id="resolve-resolution">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resolve-notes">
                <T context="form label, optional resolution notes">Notes</T>
              </Label>
              <TextInput
                id="resolve-notes"
                value={resolveComment}
                onValueChange={setResolveComment}
                placeholder={gt('Resolution notes (optional)', {
                  context: 'placeholder, resolution comment',
                })}
                rows={3}
                disabled={isBusy}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveOpen(false)}
              disabled={isBusy}
            >
              <T context="button label, cancel resolve">Cancel</T>
            </Button>
            <Button onClick={handleResolve} disabled={isBusy}>
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              <T context="button label, confirm resolve">Mark resolved</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Height + fade reveal that animates on both enter and exit. Keeps `children`
 * mounted through the closing animation (so the collapse is visible), then
 * unmounts them. Uses the grid-rows `0fr`→`1fr` trick to animate to the
 * content's natural height without measuring it.
 */
function AnimatedReveal({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  // Once the enter animation finishes we drop `overflow-hidden` so the
  // composer's recipient/select popovers aren't clipped by the reveal box.
  const [settled, setSettled] = useState(open);
  // Hold the last non-null children so the exit animation still has something to
  // render after the parent clears them (open flips false before unmount).
  const lastChildren = useRef<React.ReactNode>(children);
  if (open) lastChildren.current = children;

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true));
      const settle = setTimeout(() => setSettled(true), 220);
      return () => {
        cancelAnimationFrame(id);
        clearTimeout(settle);
      };
    }
    setShown(false);
    setSettled(false);
    const timer = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(timer);
  }, [open]);

  if (!mounted) return null;
  return (
    <div
      className={cn(
        'grid transition-all duration-200 ease-out',
        shown ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      )}
    >
      <div className={settled ? '' : 'overflow-hidden'}>
        {open ? children : lastChildren.current}
      </div>
    </div>
  );
}

/**
 * Back-to-list link plus Previous/Next controls shown when the user arrived
 * from a ranked list. The back link returns to its origin — the AR summary or
 * the Collections threads tab. Prev/Next walk that same ranked (open,
 * urgency-ordered) list; controls disable at the ends or when the current
 * thread isn't in the ranked set (e.g. it was just resolved).
 */
function ThreadListNav({
  from,
  currentThreadId,
}: {
  from: 'summary' | 'threads';
  currentThreadId: string;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const { threads } = useArCollectionThreadsRanked({ limit: 100 });

  const index = threads.findIndex((t) => t.id === currentThreadId);
  const prev = index > 0 ? threads[index - 1] : null;
  const next =
    index >= 0 && index < threads.length - 1 ? threads[index + 1] : null;

  function goTo(target: { customerId: string; id: string }) {
    void navigate({
      to: '/accounts-receivable/collections/$customerId/threads/$threadId',
      params: { customerId: target.customerId, threadId: target.id },
      // Preserve origin so Back keeps pointing where the user started.
      search: { from },
    });
  }

  return (
    <div className="flex items-center gap-2">
      {from === 'summary' ? (
        <Link
          to="/accounts-receivable/summary"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          &larr; <T context="navigation, back to AR summary">Back to summary</T>
        </Link>
      ) : (
        <Link
          to="/accounts-receivable/collections"
          search={{ tab: 'threads' }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          &larr;{' '}
          <T context="navigation, back to collection threads list">
            Back to threads
          </T>
        </Link>
      )}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={!prev}
          onClick={() => prev && goTo(prev)}
          aria-label={gt('Previous thread', {
            context: 'button label, go to previous collection thread',
          })}
        >
          <CaretLeftIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={!next}
          onClick={() => next && goTo(next)}
          aria-label={gt('Next thread', {
            context: 'button label, go to next collection thread',
          })}
        >
          <CaretRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface HeaderProps {
  referenceCode: string;
  title: string;
  description?: string | null;
  status: 'open' | 'resolved';
  type: CollectionThreadType;
  openedRelative: string;
  resolvedRelative: string | null;
  resolution: CollectionThreadResolution | null;
  createdAt: string;
  autoDetected: boolean;
  customerId: string;
  customerName: string | null;
  metadata: Record<string, unknown>;
  actions?: React.ReactNode;
}

function ThreadHeader({
  referenceCode,
  title,
  description,
  status,
  type,
  openedRelative,
  resolvedRelative,
  resolution,
  createdAt,
  autoDetected,
  customerId,
  customerName,
  metadata,
  actions,
}: HeaderProps) {
  const isResolved = status === 'resolved';
  // Present only when the open-time discrepancy interpreter matched the
  // detection against a remediation class using the customer's notes.
  const remediation = CollectionThreadRemediationMetadataSchema.safeParse(
    metadata.remediation,
  );
  const absoluteOpened = (() => {
    const parsed = parseISO(createdAt);
    if (Number.isNaN(parsed.getTime())) return createdAt;
    return format(parsed, "MMM d, yyyy 'at' h:mm a");
  })();

  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
          <span className="font-mono text-foreground/70">{referenceCode}</span>
          <span className="text-muted-foreground/60">·</span>
          <Badge
            variant="warning"
            className="bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-50 rounded-md px-2 py-0.5"
          >
            {TYPE_LABELS[type]}
          </Badge>
          {remediation.success && (
            <Badge
              variant="outline"
              className="border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-50 rounded-md px-2 py-0.5"
              title={remediation.data.explanation}
            >
              <T context="badge showing the AI-inferred likely cause of a collection thread, derived from customer notes">
                Likely cause: <Var>{remediation.data.label}</Var>
              </T>
            </Badge>
          )}
          {isResolved ? (
            <Badge
              variant="outline"
              className="border-green-600 text-green-700 rounded-full"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-green-600 mr-1.5" />
              <T context="status badge, collection thread resolved">Resolved</T>
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1.5" />
              <T context="status badge, collection thread open">Open</T>
            </Badge>
          )}
          <span className="text-muted-foreground/60">·</span>
          <span title={absoluteOpened}>
            <T>
              Opened <Var>{openedRelative}</Var>
            </T>
            {autoDetected && (
              <>
                {' '}
                <T context="opened by, attribution to Gently AI">
                  by <Var>Gently</Var>
                </T>
              </>
            )}
          </span>
          {isResolved && resolvedRelative && (
            <span>
              ·{' '}
              <T>
                resolved <Var>{resolvedRelative}</Var>
              </T>
              {resolution && <> · {RESOLUTION_LABELS[resolution]}</>}
            </span>
          )}
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {title}
        </h1>

        {customerName && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BuildingsIcon className="h-3.5 w-3.5" />
            <Link
              to="/accounts-receivable/collections/$customerId"
              params={{ customerId }}
              className="font-medium text-foreground/80 hover:text-foreground"
            >
              {customerName}
            </Link>
          </div>
        )}

        {description && (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex-shrink-0">{actions}</div>}
    </div>
  );
}
